<script setup lang="ts">
import { computed } from 'vue'

import {
  ReportMediaItem,
  type ReportColumn,
  type ReportDataState,
  type ReportRow,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { Carousel } from '@/shared/ui/carousel'
import type { UiAsyncState } from '@/shared/ui/types'

const props = withDefaults(
  defineProps<{
    rows: ReportRow[]
    dimensions: ReportColumn[]
    metrics: ReportColumn[]
    mediaType?: 'Image' | 'Video'
    slidesPerView?: number
    state?: ReportDataState
    /** Header-owned arrows; see `Carousel` — pass the ref even while it is still null. */
    prevEl?: HTMLElement | null
    nextEl?: HTMLElement | null
  }>(),
  { mediaType: 'Image', slidesPerView: 2, state: 'ready' },
)
const { t } = useI18n()
const uiState = computed<UiAsyncState>(() => (props.state === 'idle' ? 'loading' : props.state))
// Forward the arrow props only when the caller supplied them, so an omitted pair keeps the
// carousel's own overlay arrows.
const navigationBinding = computed(() =>
  props.prevEl === undefined && props.nextEl === undefined
    ? {}
    : { prevEl: props.prevEl ?? null, nextEl: props.nextEl ?? null },
)
</script>

<template>
  <Carousel
    v-bind="{ ariaLabel: t('summary.creative.carouselLabel'), ...navigationBinding }"
    :items="rows"
    :slides-per-view="slidesPerView"
    :state="uiState"
    :previous-label="t('summary.creative.previous')"
    :next-label="t('summary.creative.next')"
    :load-error-message="t('summary.states.error')"
    :retry-label="t('summary.actions.retry')"
  >
    <template #default="{ item, index }">
      <ReportMediaItem
        :row="item"
        :dimensions="dimensions"
        :metrics="metrics"
        :media-type="mediaType"
        :alt="t('summary.creative.mediaAlt', { index: index + 1 })"
        :unavailable-label="t('summary.creative.unavailable')"
        surface="sunken"
      />
    </template>
    <template #empty />
  </Carousel>
</template>
